import React, { useState, useEffect } from 'react';
import { 
Container, 
Typography, 
Button, 
Paper, 
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
TablePagination,
Card,
CardActions,
Divider,
useTheme,
Menu,
Alert,
Switch,
FormControlLabel
} from '@mui/material';
import { 
Delete, 
Search,
Add as AddIcon,
CloudUpload,
Close as CloseIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon,
Key as KeyIcon,
GetApp,
Person,
CheckCircle,
Cancel,
Business,
LocationOn,
Phone,
Email
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../services/Text';
import { 
    formatCreatedAtDate, 
    formatNumberWithComma, 
    formatNumberWithComma2, 
    getStatusById, 
    getUserDetails, 
    recordsPerPage, 
    statusData 
} from '../../utils/helpers';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../utils/Permission';
import { branchApiService } from '../../apiServices/BranchApiService';

const Branch = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        address: '',
        phone: '',
        email: '',
        is_active: '1'
    });

    const [filters, setFilters] = useState({
        name: '',
        code: '',
        is_active: '',
        dateFrom: '',
        dateTo: ''
    });

    const [errors, setErrors] = useState({
        name: false,
        code: false,
        address: false,
        phone: false,
        email: false,
        is_active: false
    });

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);

    // Status options
    const statusOptions = [
        { id: '1', name: 'Active' },
        { id: '0', name: 'Inactive' }
    ];

    // Permissions
    const [userRole, setUserRole] = useState('');
    useEffect(() => {
        const role = getUserRole();
        setUserRole(role);
    }, []);

    // Helper function
    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    useEffect(() => {
        branchApiService.setToastFunction(showToast);
    }, [showToast]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");
            const response = await branchApiService.list(userBranchId);
            if (response.success) {
                const branches = Array.isArray(response.data) ? response.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: branches
                }));

                showToast("Success", TEXT_DATA_LOADED, "success");
            } else {
                setError(response.message);
                showToast("Error", response.message, "error");
            }
        } catch (error) {
            let errorMessage = TEXT_CONNECTION_ERROR;
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    // Get status display text
    const getStatusText = (status) => {
        return status === '1' || status === 1 ? 'Active' : 'Inactive';
    };

    // Get status color
    const getStatusColor = (status) => {
        return status === '1' || status === 1 ? 'success' : 'error';
    };

    // Get status icon
    const getStatusIcon = (status) => {
        return status === '1' || status === 1 ? <CheckCircle /> : <Cancel />;
    };

    // Filter based on search term and filters
    const filteredData = pageState.pageData.filter(record => {
        const matchesSearch = searchTerm === '' ||
            record.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            getStatusText(record.is_active).toLowerCase().includes(searchTerm.toLowerCase());

        // Name filter
        const matchesName = !filters.name ||
            record.name.toLowerCase().includes(filters.name.toLowerCase());
        
        const matchesCode = !filters.code ||
            record.code.toLowerCase().includes(filters.code.toLowerCase());

        // Status filter
        const matchesStatus = !filters.is_active ||
            record.is_active.toString() === filters.is_active;

        // Date range filter
        let matchesDate = true;
        if (filters.dateFrom) {
            const recordDate = new Date(record.created_at).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate >= filterFromDate;
        }

        if (filters.dateTo) {
            const recordDate = new Date(record.created_at).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate <= filterToDate;
        }

        return matchesSearch && matchesName && matchesCode && matchesStatus && matchesDate;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        setPostStatus("ADD")
        setFormData({
            name: '',
            code: '',
            address: '',
            phone: '',
            email: '',
            is_active: '1'
        });
        setOpenAdd(true);
    }

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleCloseView = () => setOpenView(false);

    const handleOpenEdit = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id)
        setPostStatus("UPDATE")

        // Set form data
        setFormData({
            name: record.name,
            code: record.code,
            address: record.address || '',
            phone: record.phone || '',
            email: record.email || '',
            is_active: record.is_active.toString() || '1'
        });
        setOpenAdd(true);
    };

    const handleOpenDelete = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id)
        setPostStatus("DELETE")
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false)
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Reset error for this field
        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            name: '',
            code: '',
            is_active: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const validateForm = () => {
        const errors = [];
        if (!formData.name.trim()) errors.push('Branch name is required');
        if (!formData.code.trim()) errors.push('Branch code is required');
        
        // Code format validation (alphanumeric)
        const codeRegex = /^[A-Za-z0-9]+$/;
        if (formData.code && !codeRegex.test(formData.code)) {
            errors.push('Branch code must contain only letters and numbers');
        }

        // Email format validation (if provided)
        if (formData.email && formData.email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                errors.push('Please enter a valid email address');
            }
        }

        // Phone format validation (if provided)
        if (formData.phone && formData.phone.trim()) {
            const phoneRegex = /^[+]?[0-9\s\-\(\)]{10,}$/;
            if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
                errors.push('Please enter a valid phone number');
            }
        }

        if (errors.length > 0) {
            showToast("Validation Error", errors.join('\n'), "warning");
            return false;
        }
        return true;
    };

    const handleDialogClose = () => {
        setOpenAdd(false);
        setOpenView(false);
        setOpenDelete(false);
        setFilterOptions(false);
    };

    const handleFormDataReset = () => {
        setFormData({
            name: '',
            code: '',
            address: '',
            phone: '',
            email: '',
            is_active: '1'
        });
    };

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
        if (!validateForm()) return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('user_id', getUserDetails("Id"));

    // Append required fields
    formDataToSend.append('name', formData.name || '');
    formDataToSend.append('code', formData.code || '');
    formDataToSend.append('is_active', formData.is_active || '1');
    formDataToSend.append('branch_id', getUserDetails("branchId"));

    // Append optional fields
    if (formData.address) formDataToSend.append('address', formData.address);
    if (formData.phone) formDataToSend.append('phone', formData.phone);
    if (formData.email) formDataToSend.append('email', formData.email);

    // Only append updateId for UPDATE/DELETE operations
    if (postStatus === "UPDATE" || postStatus === "DELETE") {
        formDataToSend.append('updateId', updateId);
    }

    try {
        let response;

        if (postStatus === "ADD") {
            response = await branchApiService.create(formDataToSend);
        } else if (postStatus === "UPDATE") {
            response = await branchApiService.update(formDataToSend);
        } else if (postStatus === "DELETE") {
            response = await branchApiService.delete(formDataToSend);
        }

        // Check success based on the service response
        if (response.success) {
            showToast("Success", response.message, "success");
            handleFormDataReset();
            handleDialogClose();
            fetchData();
        } else {
            // Response already contains formatted error message
            showToast("Error", response.message, "error");
        }
    } catch (error) {
        // This should rarely happen now that errors are handled in the service
        showToast("Error", "An unexpected error occurred \n\n" + error.message, "error");
    }
};

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Typography sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main
            }}>
                Branch Management ( {formatNumberWithComma2(pageState.pageData.length)} Total )
            </Typography>
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {/* Title */}
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Branch Management ( {formatNumberWithComma2(pageState.pageData.length)} Total )
                    </Typography>

                    {/* Buttons - stays on left */}
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission(PERMISSIONS.SETTINGS)  && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                Add New Branch
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<FilterIcon />}
                            onClick={handlefilterOptions}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                        >
                            View Options
                        </Button>
                    </Box>

                    {/* Search Bar - pushed to right with margin-left auto */}
                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search branches..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters as a separate row below */}
                {(filters.name || filters.code || filters.is_active || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.name && (
                            <Chip
                                label={`Name: ${filters.name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, name: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.code && (
                            <Chip
                                label={`Code: ${filters.code}`}
                                onDelete={() => setFilters(prev => ({ ...prev, code: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.is_active && (
                            <Chip
                                label={`Status: ${getStatusText(filters.is_active)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, is_active: '' }))}
                                color={getStatusColor(filters.is_active)}
                                variant="outlined"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Address</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((branch, index) => (
                                    <TableRow
                                        key={index}
                                        hover
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Chip
                                                label={page * rowsPerPage + index + 1}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{formatCreatedAtDate(branch.created_at)}</TableCell>
                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Business sx={{ color: 'primary.main' }} />
                                                {branch.name}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={branch.code}
                                                color="secondary"
                                                variant="outlined"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {branch.address ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: '150px' }}>
                                                        {branch.address}
                                                    </Typography>
                                                </Box>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {branch.phone ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    {branch.phone}
                                                </Box>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {branch.email ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: '150px' }}>
                                                        {branch.email}
                                                    </Typography>
                                                </Box>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                        {branch.is_active === 1 ? (
                                            <Chip
                                            label={getStatusById(branch.is_active)}
                                            size="small"
                                            color="primary"
                                            sx={{ mt: 0.5 }}
                                            />
                                        ) : (
                                            <Chip
                                            label={getStatusById(branch.is_active)}
                                            size="small"
                                            color="error"
                                            sx={{ mt: 0.5 }}
                                            />
                                        )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                edge="end"
                                                onClick={() => handleOpenView(branch)}
                                                color="primary"
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.info.light
                                                    }
                                                }}
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                            {hasPermission(PERMISSIONS.SETTINGS) && (<>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleOpenEdit(branch)}
                                                    color="secondary"
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.success.light
                                                        }
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleOpenDelete(branch)}
                                                    color="error"
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.error.light
                                                        }
                                                    }}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </>)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {/* Pagination */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <Search sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No branches found</Typography>
                            <Typography variant="body2">Try adjusting your search query</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Options Dialog */}
            <Dialog
                open={filterOptions}
                onClose={handleCloseOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Branches
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Branches
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Name / Code
                        </Typography>

                        <TextField
                            fullWidth
                            label="Name"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                        />

                        <TextField
                            fullWidth
                            label="Code"
                            name="code"
                            value={filters.code}
                            onChange={handleFilterChange}
                        />

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Status
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="is_active"
                                value={filters.is_active}
                                onChange={handleFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                {statusOptions.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseOptions}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleCloseOptions}
                        color="primary"
                        variant="contained"
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Dialog */}
            <Dialog
                open={openAdd}
                onClose={handleCloseAdd}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>{postStatus === "ADD" ? "Add New Branch" : "Edit Branch"}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Branch Name"
                                    name="name"
                                    value={formData.name}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    error={errors.name}
                                    helperText={errors.name ? "Branch name is required" : ""}
                                    required
                                />
                            </Grid>
                            <br></br>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Branch Code"
                                    name="code"
                                    value={formData.code}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    error={errors.code}
                                    helperText={errors.code ? "Unique branch code is required" : ""}
                                    required
                                />
                            </Grid>
                            <br></br>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Address"
                                    name="address"
                                    value={formData.address}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                            <br></br>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone Number"
                                        name="phone"
                                        value={formData.phone}
                                        autoComplete="off"
                                        onChange={handleInputChange}
                                        error={errors.phone}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        autoComplete="off"
                                        onChange={handleInputChange}
                                        error={errors.email}
                                    />
                                </Grid>
                            </Grid>
                            <br></br>

                            {/* Status Field - Switch for better UX */}
                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel>Status *</InputLabel>
                                    <Select
                                    name="is_active"
                                    value={formData.is_active}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    label="Status *"
                                    >
                                    {statusData.map((item, index) => (
                                        <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                    ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Box>

                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button
                                color="error"
                                onClick={handleCloseAdd}
                                variant="contained"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                            >
                                {postStatus === "ADD" ? "Add Branch" : "Update Branch"}
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={handleCloseView}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>Branch Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ p: 2 }}>
                            <Box>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Business sx={{ color: 'primary.main' }} />
                                        <b>Name: </b>{selectedRecord.name}
                                    </Typography>
                                </Grid>
                                <br></br>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Code: </b>
                                        <Chip
                                            label={selectedRecord.code}
                                            color="secondary"
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Typography>
                                </Grid>
                                <br></br>
                                {selectedRecord.address && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <LocationOn sx={{ color: 'text.secondary', mt: 0.5 }} />
                                                <span><b>Address: </b>{selectedRecord.address}</span>
                                            </Typography>
                                        </Grid>
                                        <br></br>
                                    </>
                                )}
                                {selectedRecord.phone && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Phone sx={{ color: 'text.secondary' }} />
                                                <span><b>Phone: </b>{selectedRecord.phone}</span>
                                            </Typography>
                                        </Grid>
                                        <br></br>
                                    </>
                                )}
                                {selectedRecord.email && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Email sx={{ color: 'text.secondary' }} />
                                                <span><b>Email: </b>{selectedRecord.email}</span>
                                            </Typography>
                                        </Grid>
                                        <br></br>
                                    </>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Status: </b>
                                        <Chip
                                            icon={getStatusIcon(selectedRecord.is_active)}
                                            label={getStatusById(selectedRecord.is_active)}
                                            color={getStatusColor(selectedRecord.is_active)}
                                            size="small"
                                        />
                                    </Typography>
                                </Grid>
                                <br></br>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Created Date: </b>{formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                                </Grid>
                                <br></br>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Last Updated: </b>{formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                                </Grid>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseView}
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={handleCloseDelete}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Branch</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this branch?
                    </Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {selectedRecord.name} <br /> 
                            <Typography variant="body2" color="text.secondary">
                                Code: {selectedRecord.code}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Chip
                                    icon={<Business />}
                                    label={selectedRecord.name}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                <Chip
                                    icon={getStatusIcon(selectedRecord.is_active)}
                                    label={getStatusText(selectedRecord.is_active)}
                                    color={getStatusColor(selectedRecord.is_active)}
                                    size="small"
                                />
                            </Box>
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseDelete}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                    >
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Branch;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});